// CORS is confirmed open for https://astrotalk.store. If this section is
// reused on a different storefront domain, verify that domain is allowed too —
// otherwise the browser silently blocks the fetch with no visible error.
const CITY_AUTOCOMPLETE_URL =
  'https://api.supportchat.astrotalk.com/AstroChat/cities/allcountries/autocomplete';

/**
 * An Indian mobile as ten bare digits, or '' if it isn't one. Mirrors
 * normalizePhone() in the backend, which re-derives this on every write — the
 * value stored on the order has to be the same either way round.
 */
function normalizePhone(raw) {
  const digits = String(raw ?? '').replace(/\D/g, '');
  const local =
    digits.startsWith('91') && digits.length === 12
      ? digits.slice(2)
      : digits.startsWith('0') && digits.length === 11
        ? digits.slice(1)
        : digits;
  return /^[6-9]\d{9}$/.test(local) ? local : '';
}
/**
 * The buyer's email, trimmed, or '' if it isn't one. Same shape as the check in
 * api.kundli-details.tsx, so a value this accepts is never rejected there.
 */
function normalizeEmail(raw) {
  const value = String(raw ?? '').trim();
  return /^[^\s@.]+(?:\.[^\s@.]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i.test(value) ? value : '';
}

const POB_DEBOUNCE_MS = 500;
const POB_MIN_CHARS = 3;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TOAST_MS = 2600;

/** Report-language ids as the API numbers them, for display only. */
const LANGUAGE_LABELS = {
  '1': 'Hinglish',
  '2': 'English',
  '3': 'Hindi',
  '4': 'Bengali',
  '5': 'Gujarati',
  '6': 'Marathi',
  '7': 'Kannada',
};

/** Row order and labels, kept in step with snippets/kroh-report-person.liquid. */
const PARAM_LABELS = [
  ['name', 'Full Name'],
  ['gender', 'Gender'],
  ['number', 'Mobile'],
  ['email', 'Email'],
  ['language', 'Report language'],
  ['dob', 'Date of Birth'],
  ['tob', 'Time of Birth'],
  ['pob', 'Place of Birth'],
];

function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

/** "2026-07-01" -> "01 Jul 2026", for patching a detail panel after a save. */
function prettyDob(value) {
  const parts = String(value || '').split('-');
  if (parts.length !== 3) return value || '—';
  return `${parts[2]} ${MONTHS[Number(parts[1]) - 1] || ''} ${parts[0]}`;
}

/** 24-hour "HH:MM" (or the unknown-time sentinel) -> "08:06 AM" / "Not known". */
function prettyTob(tob) {
  const parts = from24Hour(tob);
  if (!parts) return 'Not known';
  return `${parts.hh.padStart(2, '0')}:${parts.mm} ${parts.ampm}`;
}

function clampDigits(value, max) {
  const digits = value.replace(/\D/g, '').slice(0, 2);
  if (digits === '') return digits;
  const num = Number(digits);
  if (num > max) return String(max);
  return digits;
}

/** Converts a 12-hour HH:MM + AM/PM trio to a 24-hour "HH:MM" string. */
function to24Hour(hh, mm, ampm) {
  const hour12 = Number(hh) % 12;
  const hour24 = ampm === 'PM' ? hour12 + 12 : hour12;
  return `${String(hour24).padStart(2, '0')}:${String(Number(mm)).padStart(2, '0')}`;
}

/** Converts a 24-hour "HH:MM" string back to { hh, mm, ampm } for the UI, or null if unknown. */
function from24Hour(tob) {
  // "12:00am" is the legacy spelling of "time unknown", still on orders placed
  // before the forms switched to 24-hour. Both mean the same thing here.
  const unknown = ['12:00am', '00:00'];
  if (!tob || unknown.includes(tob.trim().toLowerCase())) return null;

  const match = /^(\d{1,2}):(\d{2})$/.exec(tob.trim());
  if (!match) return null;

  const hour24 = Number(match[1]);
  const mm = match[2];
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;

  return { hh: String(hour12), mm, ampm };
}

export class KrOrderHistoryApp extends HTMLElement {
  connectedCallback() {
    this.backendUrl = (this.dataset.backendUrl || '').replace(/\/+$/, '');
    this.customerId = this.dataset.customerId || '';
    this.customerEmail = this.dataset.customerEmail || '';
    this.kundliApiKey = this.dataset.kundliApiKey || '';
    this.editWindowMinutes = Number(this.dataset.editWindowMinutes) || 30;
    this.pageSize = Math.max(2, Number(this.dataset.pageSize) || 5);

    this.cards = Array.from(this.querySelectorAll('[data-kroh-card]'));
    this.queryInput = this.querySelector('[data-kroh-query]');
    this.noMatchPanel = this.querySelector('[data-kroh-no-match]');
    this.pager = this.querySelector('[data-kroh-pager]');
    this.pagesEl = this.querySelector('[data-kroh-pages]');
    this.rangeEl = this.querySelector('[data-kroh-range]');
    this.resultLabelEl = this.querySelector('[data-kroh-result-label]');
    this.toastEl = this.querySelector('[data-kroh-toast]');
    this.toastTextEl = this.querySelector('[data-kroh-toast-text]');

    this.query = '';
    this.page = 1;

    this.modal = this.querySelector('[data-kroh-modal]');
    this.loading = this.querySelector('[data-kroh-loading]');
    this.form = this.querySelector('[data-kroh-form]');
    this.submitButton = this.querySelector('[data-kroh-submit]');
    this.formMessage = this.querySelector('[data-kroh-form-message]');
    this.orderNameLabel = this.querySelector('[data-kroh-modal-order-name]');

    this.formsContainer = this.querySelector('[data-kroh-forms-container]');
    this.formTemplate = this.querySelector('[data-kroh-form-template]');
    this.tabsEl = this.querySelector('[data-kroh-tabs]');
    this.tabs = [];
    this.activeFormIndex = 0;

    this.activeOrderId = null;
    this.pobRequestSeq = 0;
    this.formsData = [];

    this.#bindEditButtons();
    this.#bindModal();
    this.#bindSubmit();

    this.#bindSearch();
    this.#bindDetailToggles();
    this.#render();
    this.#startEditWindowTicker();
  }

  disconnectedCallback() {
    clearTimeout(this.toastTimer);
    clearInterval(this.tickerTimer);
  }

  #authHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-Kundli-Api-Key': this.kundliApiKey || '',
    };
  }

  // ---------- Search, paging, expandable rows ----------

  #bindSearch() {
    if (this.queryInput) {
      this.queryInput.addEventListener('input', () => {
        this.query = this.queryInput.value.trim().toLowerCase();
        this.page = 1;
        this.#render();
      });
    }

    if (this.pager) {
      this.querySelector('[data-kroh-prev]')?.addEventListener('click', () => {
        this.page = Math.max(1, this.page - 1);
        this.#render();
      });
      this.querySelector('[data-kroh-next]')?.addEventListener('click', () => {
        this.page = Math.min(this.#totalPages(), this.page + 1);
        this.#render();
      });
    }
  }

  #bindDetailToggles() {
    this.cards.forEach((card) => {
      const toggle = card.querySelector('[data-kroh-toggle]');
      const detail = card.querySelector('[data-kroh-detail]');
      const label = card.querySelector('[data-kroh-toggle-label]');
      if (!toggle || !detail) return;

      toggle.addEventListener('click', () => {
        const willOpen = detail.hidden;
        detail.hidden = !willOpen;
        card.toggleAttribute('data-kroh-open', willOpen);
        toggle.setAttribute('aria-expanded', String(willOpen));
        if (label) label.textContent = willOpen ? 'Hide details' : 'View details';
      });
    });
  }

  #matches() {
    if (!this.query) return this.cards;
    return this.cards.filter((card) => (card.dataset.krohSearch || '').includes(this.query));
  }

  #totalPages() {
    return Math.max(1, Math.ceil(this.#matches().length / this.pageSize));
  }

  /**
   * Single source of truth for what is on screen. Every card is already in the
   * DOM, so filtering and paging are just `hidden` flags — no refetch, and the
   * expanded state of an untouched row survives a search.
   */
  #render() {
    if (!this.cards.length) return;

    const matches = this.#matches();
    const totalPages = this.#totalPages();
    this.page = Math.min(this.page, totalPages);

    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize;
    const visible = matches.slice(start, end);

    this.cards.forEach((card) => {
      card.hidden = !visible.includes(card);
    });

    if (this.noMatchPanel) this.noMatchPanel.hidden = matches.length !== 0;

    if (this.resultLabelEl) {
      const noun = matches.length === 1 ? 'order' : 'orders';
      this.resultLabelEl.textContent = this.query
        ? `${matches.length} ${noun} matching "${this.queryInput.value.trim()}"`
        : `${matches.length} ${noun}`;
    }

    if (this.rangeEl) {
      const from = matches.length ? start + 1 : 0;
      this.rangeEl.textContent = `Showing ${from}–${Math.min(end, matches.length)} of ${matches.length}`;
    }

    if (this.pager) {
      this.pager.hidden = totalPages <= 1;
      this.querySelector('[data-kroh-prev]').disabled = this.page === 1;
      this.querySelector('[data-kroh-next]').disabled = this.page === totalPages;
      this.#renderPageButtons(totalPages);
    }
  }

  #renderPageButtons(totalPages) {
    if (!this.pagesEl) return;
    this.pagesEl.textContent = '';

    for (let n = 1; n <= totalPages; n += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'kroh-pager__num';
      button.textContent = String(n);
      if (n === this.page) button.setAttribute('aria-current', 'true');
      button.addEventListener('click', () => {
        this.page = n;
        this.#render();
      });
      this.pagesEl.append(button);
    }
  }

  /**
   * The edit window closes while the page is open, so the countdown and the Edit
   * button have to expire client-side — otherwise a customer clicks Edit minutes
   * after the backend stopped accepting changes and only finds out on submit.
   */
  #startEditWindowTicker() {
    const editable = this.cards.filter((card) => card.querySelector('[data-kroh-countdown]'));
    if (!editable.length) return;

    const tick = () => {
      editable.forEach((card) => {
        const placed = Number(card.dataset.krohPlaced) * 1000;
        const minutesLeft = Math.ceil((placed + this.editWindowMinutes * 60000 - Date.now()) / 60000);
        const countdown = card.querySelector('[data-kroh-countdown]');

        if (minutesLeft > 0) {
          if (countdown) countdown.textContent = String(minutesLeft);
          return;
        }

        card.querySelector('[data-kroh-edit-order]')?.remove();
        const note = card.querySelector('.kroh-card__note');
        if (note) {
          note.classList.remove('kroh-card__note--warn');
          note.textContent = 'The edit window for this order has closed';
        }
      });
    };

    tick();
    this.tickerTimer = setInterval(tick, 30000);
  }

  #showToast(text) {
    if (!this.toastEl || !this.toastTextEl) return;
    this.toastTextEl.textContent = text;
    this.toastEl.hidden = false;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toastEl.hidden = true;
    }, TOAST_MS);
  }

  /**
   * Mirrors a saved edit into the order's "Report parameters" panel.
   *
   * An order can carry several reports, and the modal submits them in the same
   * order the panel renders them — both sides derive the sequence from the
   * suffix scheme on the order attributes (name, name_2, name_3…). So report N
   * in the payload is the block at [data-kroh-person="N"], matched by that
   * position rather than by name, which is itself editable.
   */
  #patchDetailPanel(orderId, reports) {
    const card = this.cards.find((c) => c.dataset.krohOrderId === String(orderId));
    if (!card || !Array.isArray(reports)) return;

    const container = card.querySelector('[data-kroh-params]');

    // An order with no details renders an explanatory note and no person blocks,
    // and that is exactly the order most likely to be edited — the pay-first one
    // whose post-checkout form was never submitted. Without this the save would
    // succeed, the toast would say so, and the panel would still read "no birth
    // details on this order yet" until a reload.
    if (container && !container.querySelector('[data-kroh-person]')) {
      container.textContent = '';
      reports.forEach((_, index) => {
        container.append(this.#buildPersonBlock(index + 1, reports.length));
      });
    }

    reports.forEach((details, index) => {
      // 1-based to match the attribute suffixes the panel was rendered from.
      const block = card.querySelector(`[data-kroh-person="${index + 1}"]`);
      if (!block) return;

      const display = {
        name: details.name,
        gender: details.gender,
        number: details.number,
        email: details.email,
        language: LANGUAGE_LABELS[details.language] || LANGUAGE_LABELS['1'],
        dob: prettyDob(details.dob),
        tob: prettyTob(details.tob),
        pob: details.pob,
      };

      Object.entries(display).forEach(([key, value]) => {
        const cell = block.querySelector(`[data-kroh-param="${key}"]`);
        if (cell) cell.textContent = value || '—';
      });
    });
  }

  /**
   * An empty "Report parameters" block, matching what snippets/kroh-report-person
   * renders. Only used to fill a panel that had nothing to show at page load —
   * the server-rendered markup is always preferred, and #patchDetailPanel writes
   * the values into whichever of the two is present.
   */
  #buildPersonBlock(index, total) {
    const block = document.createElement('div');
    block.className = 'kroh-person';
    block.dataset.krohPerson = String(index);

    if (total > 1) {
      const title = document.createElement('div');
      title.className = 'kroh-person__title';
      title.textContent = `Person ${index}`;
      block.append(title);
    }

    const rows = document.createElement('dl');
    rows.className = 'kroh-rows';

    PARAM_LABELS.forEach(([key, label]) => {
      const row = document.createElement('div');
      row.className = 'kroh-row';

      const dt = document.createElement('dt');
      dt.textContent = label;

      const dd = document.createElement('dd');
      dd.dataset.krohParam = key;
      dd.textContent = '—';

      row.append(dt, dd);
      rows.append(row);
    });

    block.append(rows);
    return block;
  }

  // ---------- Edit button / modal open-close ----------

  #bindEditButtons() {
    this.querySelectorAll('[data-kroh-edit-order]').forEach((button) => {
      button.addEventListener('click', () => {
        this.#openEditModal(button.dataset.orderId, button.dataset.orderName);
      });
    });
  }

  #bindModal() {
    if (!this.modal) return;

    this.querySelectorAll('[data-kroh-close-modal]').forEach((button) => {
      button.addEventListener('click', () => this.closeModal());
    });

    this.modal.addEventListener('click', (event) => {
      if (event.target === this.modal) this.closeModal();
    });

    // Fires on every close path (button, backdrop click, native Escape), so
    // the place dropdown never lingers open behind a closed modal and any
    // still-in-flight autocomplete request is ignored when it resolves.
    this.modal.addEventListener('close', () => {
      this.formsData.forEach((fd) => this.#hideAutocomplete(fd));
      this.pobRequestSeq += 1;
    });
  }

  closeModal() {
    if (typeof this.modal.close === 'function') this.modal.close();
    else this.modal.removeAttribute('open');
  }

  async #openEditModal(orderId, orderName) {
    this.activeOrderId = orderId;

    if (this.orderNameLabel) this.orderNameLabel.textContent = orderName ? `Order ${orderName}` : '';
    if (this.formMessage) {
      this.formMessage.hidden = true;
      this.formMessage.textContent = '';
    }

    this.form.hidden = true;
    this.loading.hidden = false;
    this.loading.textContent = 'Loading current details…';
    this.formsContainer.innerHTML = '';
    this.formsData = [];
    // Rebuilt per order — the previous one may have had a different number of
    // people, and a stale tab would point at a panel that no longer exists.
    if (this.tabsEl) {
      this.tabsEl.textContent = '';
      this.tabsEl.hidden = true;
    }
    this.tabs = [];
    this.activeFormIndex = 0;

    if (typeof this.modal.showModal === 'function') this.modal.showModal();
    else this.modal.setAttribute('open', '');

    try {
      const response = await fetch(`${this.backendUrl}/api/kundli-order/details`, {
        method: 'POST',
        headers: this.#authHeaders(),
        body: JSON.stringify({
          order_id: orderId,
          customer_id: this.customerId,
          customer_email: this.customerEmail,
        }),
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok || !json?.ok) {
        throw new Error(json?.error || 'Could not load this order’s details.');
      }

      this.#prefillForm(json.reports || []);
      this.loading.hidden = true;
      this.form.hidden = false;
    } catch (error) {
      this.loading.textContent = error.message || 'Something went wrong loading this order.';
    }
  }

  #prefillForm(reportsArray) {
    if (!reportsArray || !reportsArray.length) return;

    reportsArray.forEach((details, index) => {
      const clone = this.formTemplate.content.cloneNode(true);
      const root = clone.querySelector('[data-kroh-single-form]');

      const idSuffix = `_${index}`;

      // Update IDs to avoid duplicates
      root.querySelectorAll('label[for]').forEach(lbl => {
        lbl.setAttribute('for', `${lbl.getAttribute('for')}${idSuffix}`);
      });
      root.querySelectorAll('input[id]').forEach(inp => {
        inp.id = `${inp.id}${idSuffix}`;
      });

      const formData = {
        root,
        selectedGender: details.gender || null,
        selectedLanguage: details.language || '1',
        selectedAmPm: 'AM',
        currentLat: details.lat || '',
        currentLon: details.lon || '',
        lastTrustedPobText: details.pob || '',
        hhInput: root.querySelector('[data-kroh-field="hh"]'),
        mmInput: root.querySelector('[data-kroh-field="mm"]'),
        unknownTimeCheckbox: root.querySelector('[data-kroh-unknown-time]'),
        pobInput: root.querySelector('[data-kroh-field="pob"]'),
        autocompleteList: root.querySelector('[data-kroh-autocomplete-list]'),
        timeRow: root.querySelector('[data-kroh-time-row]'),
        pobWrapper: root.querySelector('[data-kroh-pob-wrapper]')
      };

      this.formsData.push(formData);

      root.querySelector('[data-kroh-field="name"]').value = details.name || '';
      root.querySelector('[data-kroh-field="number"]').value = details.number || '';
      root.querySelector('[data-kroh-field="email"]').value = details.email || '';
      root.querySelector('[data-kroh-field="dob"]').value = details.dob || '';
      formData.pobInput.value = details.pob || '';

      const genderButtons = root.querySelectorAll('[data-kroh-gender]');
      if (formData.selectedGender) {
        const match = Array.from(genderButtons).find((b) => b.dataset.krohGender === formData.selectedGender);
        if (match) match.classList.add('kroh-pill-toggle__btn--active');
      }

      // Always has a value, so unlike gender there is no unselected state.
      const languageSelect = root.querySelector('[data-kroh-field="language"]');
      if (languageSelect) languageSelect.value = formData.selectedLanguage;

      const timeParts = from24Hour(details.tob);
      const ampmButtons = root.querySelectorAll('[data-kroh-ampm]');

      if (!timeParts) {
        formData.unknownTimeCheckbox.checked = true;
        formData.hhInput.value = '';
        formData.mmInput.value = '';
        formData.selectedAmPm = 'AM';
        formData.timeRow.toggleAttribute('data-kroh-time-disabled', true);
        [formData.hhInput, formData.mmInput, ...ampmButtons].forEach((el) => (el.disabled = true));
      } else {
        formData.unknownTimeCheckbox.checked = false;
        formData.hhInput.value = timeParts.hh;
        formData.mmInput.value = timeParts.mm;
        formData.selectedAmPm = timeParts.ampm;
        ampmButtons.forEach((b) =>
          b.classList.toggle('kroh-pill-toggle__btn--active', b.dataset.krohAmpm === timeParts.ampm)
        );
      }

      this.#bindGenderToggle(formData);
      this.#bindLanguageToggle(formData);
      this.#bindAmPmToggle(formData);
      this.#bindTimeInputs(formData);
      this.#bindUnknownTime(formData);
      this.#bindPlaceAutocomplete(formData);

      this.formsContainer.appendChild(clone);
    });

    this.#buildTabs();
    this.#activateTab(0);
  }

  // ---------- Person tabs ----------

  /**
   * One tab per person, or none at all for a single-report order — there is
   * nothing to switch between, and a lone "Person 1" tab would only imply
   * others exist.
   */
  #buildTabs() {
    if (!this.tabsEl) return;

    this.tabsEl.textContent = '';
    this.tabs = [];

    const multiple = this.formsData.length > 1;
    this.tabsEl.hidden = !multiple;
    if (!multiple) return;

    this.formsData.forEach((formData, index) => {
      const tab = document.createElement('button');
      // Inside a <form>, an unqualified button submits it.
      tab.type = 'button';
      tab.className = 'kroh-tab';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', 'false');
      tab.textContent = `Person ${index + 1}`;
      tab.addEventListener('click', () => this.#activateTab(index));

      this.tabsEl.append(tab);
      this.tabs.push(tab);
    });
  }

  /**
   * Shows one person's fields and hides the rest.
   *
   * The hidden panels stay in the DOM rather than being re-rendered on each
   * switch: they hold whatever the customer has already typed, along with the
   * lat/lon captured from the place picker, none of which is recoverable once
   * the nodes are gone.
   */
  #activateTab(index) {
    this.activeFormIndex = index;

    this.formsData.forEach((formData, i) => {
      formData.root.hidden = this.formsData.length > 1 && i !== index;
    });

    (this.tabs ?? []).forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
    });
  }

  /** Flags a tab whose person failed validation, and clears the rest. */
  #markTabError(index) {
    (this.tabs ?? []).forEach((tab, i) => {
      const dot = tab.querySelector('.kroh-tab__dot');
      if (i === index) {
        if (!dot) {
          const marker = document.createElement('span');
          marker.className = 'kroh-tab__dot';
          tab.prepend(marker);
        }
        tab.setAttribute('data-kroh-tab-error', '');
      } else {
        dot?.remove();
        tab.removeAttribute('data-kroh-tab-error');
      }
    });
  }

  // ---------- Form Instances Bindings ----------

  #bindGenderToggle(formData) {
    const buttons = formData.root.querySelectorAll('[data-kroh-gender]');
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        buttons.forEach((b) => b.classList.remove('kroh-pill-toggle__btn--active'));
        button.classList.add('kroh-pill-toggle__btn--active');
        formData.selectedGender = button.dataset.krohGender;
      });
    });
  }

  #bindLanguageToggle(formData) {
    const select = formData.root.querySelector('[data-kroh-field="language"]');
    select?.addEventListener('change', () => {
      formData.selectedLanguage = select.value;
    });
  }

  #bindAmPmToggle(formData) {
    const buttons = formData.root.querySelectorAll('[data-kroh-ampm]');
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        buttons.forEach((b) => b.classList.remove('kroh-pill-toggle__btn--active'));
        button.classList.add('kroh-pill-toggle__btn--active');
        formData.selectedAmPm = button.dataset.krohAmpm;
      });
    });
  }

  #bindTimeInputs(formData) {
    formData.hhInput.addEventListener('input', () => {
      formData.hhInput.value = clampDigits(formData.hhInput.value, 12);
    });
    formData.mmInput.addEventListener('input', () => {
      formData.mmInput.value = clampDigits(formData.mmInput.value, 59);
    });
  }

  #bindUnknownTime(formData) {
    formData.unknownTimeCheckbox.addEventListener('change', () => {
      const isChecked = formData.unknownTimeCheckbox.checked;
      formData.timeRow.toggleAttribute('data-kroh-time-disabled', isChecked);
      [formData.hhInput, formData.mmInput, ...formData.timeRow.querySelectorAll('[data-kroh-ampm]')].forEach((el) => {
        el.disabled = isChecked;
      });
    });
  }

  // ---------- Place of birth autocomplete ----------

  #bindPlaceAutocomplete(formData) {
    if (!formData.pobInput || !formData.autocompleteList) return;

    const search = debounce(() => this.#searchPlaces(formData), POB_DEBOUNCE_MS);

    formData.pobInput.addEventListener('input', () => {
      if (formData.lastTrustedPobText !== null) {
        formData.currentLat = '';
        formData.currentLon = '';
        formData.lastTrustedPobText = null;
      }

      const query = formData.pobInput.value.trim();
      if (query.length < POB_MIN_CHARS) {
        this.pobRequestSeq += 1;
        this.#hideAutocomplete(formData);
        return;
      }
      search();
    });

    document.addEventListener('click', (event) => {
      if (!formData.pobWrapper.contains(event.target)) this.#hideAutocomplete(formData);
    });
  }

  async #searchPlaces(formData) {
    const query = formData.pobInput.value.trim();
    if (query.length < POB_MIN_CHARS) return;

    const requestId = ++this.pobRequestSeq;

    let results = [];
    try {
      const response = await fetch(`${CITY_AUTOCOMPLETE_URL}?limit=10&key=${encodeURIComponent(query)}`);
      if (response.ok) {
        const json = await response.json();
        results = Array.isArray(json?.data) ? json.data : [];
      } else {
        console.warn(`[kr-order-history] place autocomplete request failed: HTTP ${response.status}`);
      }
    } catch (error) {
      results = [];
      console.warn('[kr-order-history] place autocomplete request errored', error);
    }

    if (requestId !== this.pobRequestSeq) return;

    this.#renderAutocomplete(formData, results);
  }

  #renderAutocomplete(formData, results) {
    if (!results.length) {
      this.#hideAutocomplete(formData);
      return;
    }

    formData.autocompleteList.innerHTML = '';
    results.forEach((place) => {
      const label = [place.name, place.state, place.countryName].filter(Boolean).join(', ');
      const item = document.createElement('li');
      item.textContent = label;
      item.addEventListener('click', () => {
        formData.pobInput.value = label;
        formData.currentLat = String(place.latitude ?? '');
        formData.currentLon = String(place.longitude ?? '');
        formData.lastTrustedPobText = label;
        this.#hideAutocomplete(formData);
      });
      formData.autocompleteList.appendChild(item);
    });
    formData.autocompleteList.hidden = false;
  }

  #hideAutocomplete(formData) {
    if (!formData.autocompleteList) return;
    formData.autocompleteList.hidden = true;
    formData.autocompleteList.innerHTML = '';
  }

  // ---------- Submit ----------

  #bindSubmit() {
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#handleSubmit();
    });
  }

  #isTimeValid(formData) {
    if (formData.unknownTimeCheckbox.checked) return true;
    return Boolean(formData.hhInput.value.trim() && formData.mmInput.value.trim());
  }

  #validate(formData, index) {
    const prefix = this.formsData.length > 1 ? `Person ${index + 1}: ` : "";
    const name = formData.root.querySelector('[data-kroh-field="name"]').value.trim();
    const number = formData.root.querySelector('[data-kroh-field="number"]').value.trim();
    const email = formData.root.querySelector('[data-kroh-field="email"]').value.trim();
    const dob = formData.root.querySelector('[data-kroh-field="dob"]').value;
    const pob = formData.pobInput.value.trim();

    if (name.length <= 1) return { ok: false, error: `${prefix}Please enter a valid name.` };
    if (!formData.selectedGender) return { ok: false, error: `${prefix}Please select a gender.` };
    if (!normalizePhone(number)) return { ok: false, error: `${prefix}Please enter a valid 10-digit mobile number.` };
    if (!normalizeEmail(email)) return { ok: false, error: `${prefix}Please enter a valid email address.` };
    if (!dob) return { ok: false, error: `${prefix}Please enter date of birth.` };
    if (!this.#isTimeValid(formData)) return { ok: false, error: `${prefix}Please enter time of birth.` };
    if (pob.length <= 1) return { ok: false, error: `${prefix}Please enter a place of birth.` };

    if (String(formData.currentLat ?? '') === '' || String(formData.currentLon ?? '') === '') {
       return { ok: false, error: `${prefix}Please pick the place of birth from the suggestions.` };
    }

    return { ok: true };
  }

  #showMessage(text, tone) {
    if (!this.formMessage) return;
    this.formMessage.textContent = text;
    this.formMessage.dataset.tone = tone;
    this.formMessage.hidden = false;
  }

  async #handleSubmit() {
    this.#showMessage('', 'error');
    this.formMessage.hidden = true;

    for (let i = 0; i < this.formsData.length; i++) {
      const v = this.#validate(this.formsData[i], i);
      if (!v.ok) {
        // Only one person's fields are on screen, so switch to the one at fault
        // before complaining about it — otherwise "Person 3: enter a valid name"
        // appears under a form showing Person 1 and reads as a wrong message.
        this.#activateTab(i);
        this.#markTabError(i);
        this.#showMessage(v.error, 'error');
        return;
      }
    }

    this.#markTabError(-1);

    const reports = this.formsData.map(formData => ({
      name: formData.root.querySelector('[data-kroh-field="name"]').value.trim(),
      gender: formData.selectedGender,
      language: formData.selectedLanguage,
      dob: formData.root.querySelector('[data-kroh-field="dob"]').value,
      // 24-hour midnight, not "12:00am" — see the note in kundli-landing.js.
      tob: formData.unknownTimeCheckbox.checked
        ? '00:00'
        : to24Hour(formData.hhInput.value.trim(), formData.mmInput.value.trim(), formData.selectedAmPm),
      pob: formData.pobInput.value.trim(),
      lat: formData.currentLat,
      lon: formData.currentLon,
      number: formData.root.querySelector('[data-kroh-field="number"]').value.trim(),
      email: formData.root.querySelector('[data-kroh-field="email"]').value.trim(),
    }));

    const submitLabel = this.submitButton.querySelector('[data-kroh-submit-label]');
    const originalLabel = submitLabel ? submitLabel.textContent : '';

    this.submitButton.disabled = true;
    if (submitLabel) submitLabel.textContent = 'Saving…';

    try {
      const response = await fetch(`${this.backendUrl}/api/kundli-order/update`, {
        method: 'POST',
        headers: this.#authHeaders(),
        body: JSON.stringify({
          order_id: this.activeOrderId,
          customer_id: this.customerId,
          customer_email: this.customerEmail,
          reports,
        }),
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok || !json?.ok) {
        throw new Error(json?.error || 'Could not save your changes.');
      }

      this.#patchDetailPanel(this.activeOrderId, reports);
      this.closeModal();
      this.#showToast('Your details have been updated');
    } catch (error) {
      this.#showMessage(error.message || 'Something went wrong — please try again.', 'error');
    } finally {
      this.submitButton.disabled = false;
      if (submitLabel) submitLabel.textContent = originalLabel;
    }
  }
}

if (!customElements.get('kr-order-history-app')) customElements.define('kr-order-history-app', KrOrderHistoryApp);
